/**
 * INPUT, SELECT, TEXTAREA, LABEL (P36) — reference §7 and §10.
 *
 * =========================================================================
 * THE LABEL IS WIRED, AND THAT IS THE WHOLE COMPONENT
 * =========================================================================
 * 25 of 34 `<label>` elements in the app carry no `htmlFor`. Clicking such a
 * label does nothing, and a screen reader announces the control as "edit text"
 * with no name. `Field` makes the wiring impossible to forget by owning the id:
 * `useId()` generates it, the `<label>` points at it and the control receives
 * it. A call site that passes its own `id` keeps it.
 *
 * =========================================================================
 * AN ERROR IS A BORDER **AND** A SENTENCE **AND** AN ICON
 * =========================================================================
 * Reference §10, and WCAG 1.4.1: never colour alone. The sentence is tied to
 * the control with `aria-describedby` and the control carries `aria-invalid`,
 * so the error is announced when focus arrives rather than only seen.
 *
 * =========================================================================
 * THE PLACEHOLDER COLOUR
 * =========================================================================
 * `design.md` §17's fifth deviation row: Tailwind preflight paints every
 * placeholder `#9ca3af`, which measures **2.54:1** — the same value as the
 * `gray-400` P35 removed from 246 places. It comes from no source file, so it
 * could not be re-pointed with the others; it needed a base-layer rule, and the
 * rule belongs with this component. It is in `web/src/index.css` under
 * `@layer base`, and it takes `--muted` (6.18:1) rather than `--faint`
 * (2.95:1): a placeholder is READ, and `--faint` is reserved for disabled text
 * and nothing else (§2).
 *
 * Every control is 36 px (`--ctl-h`) with an `--edge` border — 3.12:1, which is
 * what WCAG 1.4.11 asks of a control boundary.
 */
import {
    useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import { AlertCircle } from 'lucide-react';
import { FOCUS_RING } from './focus';

const CONTROL =
    `w-full rounded-ctl border bg-surface px-2.5 text-sm text-ink ` +
    `disabled:cursor-not-allowed disabled:bg-head disabled:text-faint ${FOCUS_RING}`;

const edgeFor = (invalid: boolean) => (invalid ? 'border-err' : 'border-edge');

export function Label({ htmlFor, children, className = '' }: { htmlFor?: string; children: ReactNode; className?: string }) {
    return (
        <label htmlFor={htmlFor} className={`block text-[13px] font-bold text-body ${className}`.trim()}>
            {children}
        </label>
    );
}

export interface FieldProps {
    label: ReactNode;
    /** The sentence shown when the value is wrong. Its presence IS the error state. */
    error?: string | null;
    /** A sentence under the control when there is no error. */
    hint?: ReactNode;
    /** Receives `id`, `aria-invalid` and `aria-describedby`. */
    children: (props: { id: string; 'aria-invalid': boolean; 'aria-describedby': string | undefined }) => ReactNode;
    className?: string;
}

/**
 * The label / control / message triple. The control is a render prop rather
 * than a `type` string so a call site can put anything wired-up inside —
 * including the two date inputs a range needs.
 */
export function Field({ label, error, hint, children, className = '' }: FieldProps) {
    const id = useId();
    const msgId = `${id}-msg`;
    return (
        <div className={`space-y-1 ${className}`.trim()}>
            <Label htmlFor={id}>{label}</Label>
            {children({ id, 'aria-invalid': Boolean(error), 'aria-describedby': error || hint ? msgId : undefined })}
            {error
                ? (
                    <p id={msgId} className="flex items-center gap-1 text-xs font-medium text-err">
                        <AlertCircle aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                        {error}
                    </p>
                )
                : hint
                    ? <p id={msgId} className="text-xs text-muted">{hint}</p>
                    : null}
        </div>
    );
}

export function Input({ className = '', invalid = false, ...rest }: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
    return <input className={`${CONTROL} h-9 ${edgeFor(invalid || rest['aria-invalid'] === true)} ${className}`.trim()} {...rest} />;
}

export function Select({ className = '', invalid = false, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }) {
    return (
        <select className={`${CONTROL} h-9 ${edgeFor(invalid || rest['aria-invalid'] === true)} ${className}`.trim()} {...rest}>
            {children}
        </select>
    );
}

export function Textarea({ className = '', invalid = false, rows = 4, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
    return <textarea rows={rows} className={`${CONTROL} py-2 ${edgeFor(invalid || rest['aria-invalid'] === true)} ${className}`.trim()} {...rest} />;
}
